import mimeDb from 'mime-db/db.json'
import { MIME_TYPE } from 'src/Utils/globalEnums'

export function extension(mime: string): string | undefined {
  const result = (
    mimeDb as Record<string, { extensions?: string[] } | undefined>
  )[mime]
  return result?.extensions?.[0]
}

// Lazily-built reverse index of file extension -> mime type, from mime-db.
// First mime registered for an extension wins, which matches mime-db's own
// preference order.
let extensionToMime: Record<string, string> | undefined

function reverseIndex(): Record<string, string> {
  if (!extensionToMime) {
    extensionToMime = {}
    const db = mimeDb as Record<string, { extensions?: string[] } | undefined>
    for (const [mime, meta] of Object.entries(db)) {
      for (const ext of meta?.extensions ?? []) {
        if (!(ext in extensionToMime)) {
          extensionToMime[ext] = mime
        }
      }
    }
  }
  return extensionToMime
}

// mimeFromFilename resolves a file's mime type from its extension. Browsers
// frequently report an empty or inconsistent File.type (notably for .md, .csv,
// and .txt), so the filename extension is a more reliable signal for routing
// and for labeling downloads.
export function mimeFromFilename(filename: string): string | undefined {
  const ext = filename.split('.').pop()?.toLowerCase()
  return ext ? reverseIndex()[ext] : undefined
}

// extensionsForMime returns the dotted file extensions registered for a mime
// (e.g. 'text/markdown' -> ['.md', '.markdown']), for use as a react-dropzone
// accept value so files are matched by extension as well as by File.type.
export function extensionsForMime(mime: string): string[] {
  const db = mimeDb as Record<string, { extensions?: string[] } | undefined>
  return (db[mime]?.extensions ?? []).map((ext) => `.${ext}`)
}

// resolveUploadMime picks the mime to send for an uploaded file. The filename
// extension is preferred over the browser's File.type, which is unreliable --
// browsers mislabel a CSV as application/vnd.ms-excel or report
// application/octet-stream for a file whose extension resolves cleanly.
// Returns '' when neither is known, leaving the server to decide.
export function resolveUploadMime(file: File): string {
  return mimeFromFilename(file.name) || file.type || ''
}

// Mime types accepted for any upload that has to be indexed and searched
// (knowledge base documents, vendor attestations). This mirrors the
// user-uploadable converters hs-indexer registers in get_converter_mapping()
// (python/hs-indexer/hs_indexer/indexer/pipeline.py) -- an unsupported mime
// fails the upload outright rather than degrading, because an indexing error
// rejects the content, so this list must stay a subset of what hs-indexer
// can convert. ntrced enforces the same list in
// ntrced/lib/utils/uploadmimes.go.
//
// Excluded on purpose: hs-indexer's internal artifact converters
// (application/protobuf, multipart/tr-mixed, tr-artifact/*, text/uri-list) --
// those are Trustero-generated evidence artifacts, not files a user uploads.
//
// Images are enumerated concretely rather than as image/* because
// FileSelector's validateFiles gates on accept.includes(file.type), an exact
// string match; hs-indexer itself matches any image/* via its ImageToDocument
// converter.
export const INDEXABLE_UPLOAD_MIMES: MIME_TYPE[] = [
  // Text / tabular
  MIME_TYPE.TEXT_CSV,
  MIME_TYPE.APPLICATION_CSV,
  MIME_TYPE.TEXT_PLAIN,
  MIME_TYPE.TEXT_MARKDOWN,
  // HTML pages (policy exports from intranet tools); hs-indexer converts the
  // page to markdown and decodes it by its byte-order mark, so UTF-16 exports
  // from Windows tools work.
  MIME_TYPE.TEXT_HTML,
  // Both RTF mimes: browsers and mime-db report application/rtf, while
  // hs-indexer's converter keys on text/rtf. ntrced normalizes the former to
  // the latter before indexing.
  MIME_TYPE.TEXT_RTF,
  MIME_TYPE.APPLICATION_RTF,
  MIME_TYPE.APPLICATION_JSON,
  // Documents
  MIME_TYPE.APPLICATION_PDF,
  MIME_TYPE.APPLICATION_WORD,
  MIME_TYPE.APPLICATION_DOCUMENT,
  MIME_TYPE.APPLICATION_ODT,
  // Outlook messages: hs-indexer indexes the headers and body; ntrced packs
  // the attachments as parts.
  MIME_TYPE.OUTLOOK_MSG,
  // Zip archives: ntrced packs every file inside as a part, and hs-indexer
  // indexes each through its own converter.
  MIME_TYPE.ZIP,
  // Spreadsheets
  MIME_TYPE.APPLICATION_VND_MS_EXCEL,
  MIME_TYPE.APPLICATION_EXCEL,
  // Apple Numbers is intentionally omitted: FileSelector.correctAppleFiles
  // rewrites a .numbers file's type to application/vnd.apple.numbers, but
  // hs-indexer's converter keys on application/x-iwork-numbers-sffnumbers, so
  // it cannot round-trip without a mime-normalization layer neither side has.
  // Presentations
  MIME_TYPE.POWERPOINT_PRESENTATION,
  // Images: only the formats hs-indexer's ImageToDocument can OCR. It forwards
  // the image to OpenAI vision, which supports PNG/JPEG/WEBP/GIF only -- not
  // HEIC/TIFF/BMP/SVG. ntrced enforces the same allowlist.
  MIME_TYPE.IMAGE_PNG,
  MIME_TYPE.IMAGE_JPEG,
  MIME_TYPE.IMAGE_GIF,
  MIME_TYPE.IMAGE_WEBP,
]

// What the accepted mimes above amount to, in words. Spelled out rather than
// generated from the list because the mimes do not map one-to-one onto names a
// reader recognises -- four of them are "Excel" and two are "CSV" -- and a
// generated string would read as a wall of subtypes. Keep in step with
// INDEXABLE_UPLOAD_MIMES above.
export const INDEXABLE_UPLOAD_TYPES_TEXT =
  'PDF, Word, Excel, PowerPoint, Outlook messages, CSV, JSON, Markdown, HTML, RTF, plain text, zip archives, and PNG, JPEG, GIF or WEBP images.'

// Content stored as a multipart/tr-parts container (a document packed with
// its extracted sub-objects, see tech-designs/multipart-content.md) carries
// the uploaded document's mime as the `original` parameter. Downloads and
// GetBytes return that document, so it is the mime to route and label on.
export const MULTIPART_PARTS_MIME = 'multipart/tr-parts'

export function isMultipartPartsMime(mime: string): boolean {
  return mime.split(';')[0].trim() === MULTIPART_PARTS_MIME
}

export function originalMime(mime: string): string {
  if (!isMultipartPartsMime(mime)) {
    return mime
  }
  const match = /;\s*original=(?:"([^"]*)"|([^;\s]+))/.exec(mime)
  return match?.[1] ?? match?.[2] ?? mime
}
